import { BifrostWindowObject } from '@jusbrasil-web/shared-bifrost'
import { FEATURE_CLASS, GA4FeatureUseEvent } from '@jusbrasil-web/shared-ga4-events'
import { isElementSelected } from '@jusbrasil-web/shared-utils-dom'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'

import { CopyListenerHelper } from '../listener-helper/listener-helper'

type EventData = {
  componentClass: string
  componentName?: string
  copyFeatureName?: string
}

export interface CopyRequireLoginTriggerConfig {
  document?: Record<string, unknown>
  element: HTMLElement
  options?: Record<string, unknown>
  ga4EventData?: EventData
  sendGA4Event?: CallableFunction
}

export class CopyRequireLoginTriggerUtil {
  private listenerHelper: CopyListenerHelper | null

  private readonly document: Record<string, unknown>
  private readonly element: HTMLElement
  private readonly options: Record<string, unknown>
  private readonly ga4EventData?: EventData
  private readonly sendGA4Event?: CallableFunction

  constructor(config: CopyRequireLoginTriggerConfig) {
    this.listenerHelper = null
    this.document = config.document || {}
    this.element = config.element
    this.options = config.options || {}
    this.ga4EventData = config.ga4EventData
    this.sendGA4Event = config.sendGA4Event
  }

  public addCopyListener(): void {
    if (!this.listenerHelper) {
      this.listenerHelper = new CopyListenerHelper(this.onCopy, this.element)
    }

    this.listenerHelper.enable()
  }

  public removeCopyListener(): void {
    if (!this.listenerHelper) {
      return
    }

    this.listenerHelper.disable()
  }

  public readonly onCopy = (event: Event): void => {
    if (!isElementSelected(this.element)) {
      return
    }

    this.sendMetrics()
    event.preventDefault()
    window.getSelection()?.removeAllRanges()

    const options = {
      ...this.options,
      eventData: {
        from_component: 'CopyRequireLoginTrigger',
        ...(this.options.eventData || {}),
      },
    }

    if (window.Bifrost) {
      window.Bifrost.open(options)
      return
    }

    throw new Error('Bifrost is not loaded')
  }

  private sendMetrics(): void {
    const { docId, artifact } = this.document

    sendMetrics('Doc.TriedCopyContentUnlogged', '', {
      from_component: 'CopyRequireLoginTrigger',
      ...(this.options.eventData || {}),
      artifact,
      doc_id: docId,
    })

    if (this.ga4EventData && this.sendGA4Event) {
      const { componentClass, componentName, copyFeatureName } = this.ga4EventData

      this.sendGA4Event(
        new GA4FeatureUseEvent({
          componentClass,
          componentName,
          featureClass: FEATURE_CLASS.COPY_CONTENT,
          featureName: copyFeatureName,
          resourceId: `${artifact}-${docId}`,
          successfulAttempt: false,
        })
      )
    }
  }
}

declare global {
  interface Window {
    Bifrost?: BifrostWindowObject
  }
}
